/**
 * AskIsaacMovedBanner — shows a "We've Moved" notice for visitors hitting the
 * legacy askisaac.io domain, and offers a one-click jump to the same path on
 * sterilemate.com. Preserves the exact URL path so deep links keep working
 * after the switch.
 *
 * Behavior:
 *   - Only renders on askisaac.io hostnames (and any *.askisaac.io subdomain)
 *   - Sticky banner at the top with clear announcement + branded CTA
 *   - "Continue to sterilemate.com" button jumps to https://sterilemate.com<path>
 *   - Auto-redirects after 8 seconds so bookmarks eventually get repointed
 *   - Users can dismiss the countdown by clicking anywhere or the "X" button
 *     (dismissal persists in sessionStorage for the tab lifetime)
 */
import { useEffect, useState } from "react";
import { ExternalLink, X } from "lucide-react";

const NEW_HOST = "sterilemate.com";
const AUTO_REDIRECT_SECS = 8;

export default function AskIsaacMovedBanner() {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECS);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // Detect askisaac.io hostname (root + subdomains)
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isAskIsaac = /(^|\.)askisaac\.io$/i.test(host);
    if (!isAskIsaac) return;

    // Respect prior dismissal within the same tab session
    const dismissed =
      typeof window !== "undefined" &&
      window.sessionStorage.getItem("askisaac-moved-dismissed") === "1";
    if (dismissed) {
      setVisible(true);
      setPaused(true); // show banner but stop countdown
      return;
    }

    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible || paused) return;
    if (countdown <= 0) {
      handleContinue();
      return;
    }
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, paused, countdown]);

  const handleContinue = () => {
    const path = window.location.pathname + window.location.search + window.location.hash;
    window.location.replace(`https://${NEW_HOST}${path}`);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    window.sessionStorage.setItem("askisaac-moved-dismissed", "1");
    setPaused(true);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="askisaac-moved-banner"
      role="alert"
      className="sticky top-0 z-[100] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white border-b-4 border-amber-400 shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="hidden sm:flex w-9 h-9 rounded-full bg-amber-400 items-center justify-center flex-shrink-0">
            <ExternalLink className="w-4 h-4 text-slate-900" />
          </div>
          <div className="text-sm sm:text-base">
            <span className="font-bold text-amber-300">We&apos;ve moved!</span>{" "}
            <span className="text-slate-100">
              AskIsaac is now <span className="font-semibold text-white">Sterile Mate</span> at{" "}
              <span className="font-mono text-white">sterilemate.com</span>.
            </span>
            {!paused && (
              <span className="hidden md:inline text-xs text-slate-400 ml-2">
                Auto-redirecting in {countdown}s…
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleContinue}
            data-testid="askisaac-continue-btn"
            className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 whitespace-nowrap shadow-md"
          >
            Continue to sterilemate.com
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onClick={handleDismiss}
            data-testid="askisaac-dismiss-btn"
            aria-label="Dismiss this banner"
            title="Stay on askisaac.io for now"
            className="w-9 h-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
